import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { Loader2, X } from 'lucide-react';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useFocusTrap } from '../hooks/useFocusTrap';
import IngestionActivityCard from './IngestionActivityCard';

const VISIBLE_PENDING_UPLOADS = 3;
/** The pending list returns at most this many, so a full page may hide older uploads. */
const PENDING_UPLOAD_PAGE_SIZE = 10;
const READY_FOR_REVIEW_STATUS = 'completed';

/** Label the pending count. A full page is shown as 10+ because the list is capped. */
function pendingUploadCountLabel(count: number): string {
  if (count >= PENDING_UPLOAD_PAGE_SIZE) return `${PENDING_UPLOAD_PAGE_SIZE}+`;
  return String(count);
}

interface PendingUpload {
  id: string;
  fileName: string;
  status: string;
  initiatedAt: Date | null;
}

interface AfterReviewModalProps {
  /** Record created by the confirm that opened this modal. Absent after discard. */
  recordId: string | null;
  /** True when the review was discarded rather than filed. */
  discarded: boolean;
  onClose: () => void;
}

type LoadPhase = 'loading' | 'ready' | 'error';
type PendingUploadLoad = { ok: true; items: PendingUpload[] } | { ok: false } | null;

/** Decide what the modal shows while the pending-upload query is in flight. */
function afterReviewPhase(authLoading: boolean, hasUser: boolean, loaded: PendingUploadLoad): LoadPhase {
  if (authLoading) return 'loading';
  if (!hasUser) return 'error';
  if (loaded === null) return 'loading';
  return loaded.ok ? 'ready' : 'error';
}

function fileNameFromProcess(data: Record<string, unknown>, fallbackId: string): string {
  const file = data.file;
  if (!file || typeof file !== 'object' || !('name' in file)) return fallbackId;
  const name = (file as { name?: unknown }).name;
  return typeof name === 'string' && name.trim() ? name.trim() : fallbackId;
}

function initiatedAtFromProcess(value: unknown): Date | null {
  if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    return value.toDate();
  }
  if (typeof value === 'string' || value instanceof Date) return new Date(value);
  return null;
}

/**
 * Load the signed-in user's uploads that are ready for review.
 * Reads Firestore directly so each card can show the file name.
 */
async function loadPendingUploads(userEmail: string): Promise<PendingUpload[]> {
  const pendingQuery = query(
    collection(db, 'ingestion_process'),
    where('initiated_by', '==', userEmail),
    where('status', '==', READY_FOR_REVIEW_STATUS),
    orderBy('initiated_at', 'desc'),
    limit(PENDING_UPLOAD_PAGE_SIZE),
  );
  const snapshot = await getDocs(pendingQuery);
  return snapshot.docs.map((processDoc) => {
    const data = processDoc.data();
    return {
      id: processDoc.id,
      fileName: fileNameFromProcess(data, processDoc.id),
      status: typeof data.status === 'string' ? data.status : READY_FOR_REVIEW_STATUS,
      initiatedAt: initiatedAtFromProcess(data.initiated_at),
    };
  });
}

function PendingUploadsLink() {
  return (
    <div className="mt-8 flex items-center justify-center">
      <Link
        to="/my-ara"
        className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-emerald-800 hover:bg-emerald-700 transition-colors"
      >
        View all pending uploads
      </Link>
    </div>
  );
}

export default function AfterReviewModal({ recordId, discarded, onClose }: AfterReviewModalProps) {
  const { user, loading: authLoading } = useAuth();
  const [loaded, setLoaded] = useState<PendingUploadLoad>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, { onEscape: onClose });

  useEffect(() => {
    if (authLoading || !user?.email) return;

    let cancelled = false;
    loadPendingUploads(user.email)
      .then((items) => {
        if (!cancelled) setLoaded({ ok: true, items });
      })
      .catch(() => {
        if (!cancelled) setLoaded({ ok: false });
      });

    return () => {
      cancelled = true;
    };
  }, [authLoading, user?.email]);

  const phase = afterReviewPhase(authLoading, Boolean(user?.email), loaded);
  const pending = loaded?.ok ? loaded.items : [];
  const hasPending = phase === 'ready' && pending.length > 0;
  const visiblePending = pending.slice(0, VISIBLE_PENDING_UPLOADS);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="after-review-title"
        tabIndex={-1}
        className={`relative z-10 flex min-h-0 max-h-[calc(100dvh-2rem)] w-full flex-col overflow-hidden bg-white rounded-2xl shadow-2xl border border-gray-200 animate-in zoom-in-95 outline-none ${hasPending ? 'max-w-4xl' : 'max-w-lg'}`}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 z-10 bg-white p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="min-h-0 overflow-y-auto overscroll-contain p-6 sm:p-8">
          {discarded ? (
            <h2 id="after-review-title" className="text-lg font-semibold text-gray-900 text-center pr-8">
              The file you uploaded has been discarded.
            </h2>
          ) : (
            <h2 id="after-review-title" className="text-lg font-semibold text-gray-900 pr-8">
              Record confirmed and filed successfully.
              {recordId && (
                <>
                  {' '}
                  <Link
                    to={`/records/${recordId}`}
                    className="text-emerald-800 underline underline-offset-2 hover:text-emerald-700"
                  >
                    View record
                  </Link>
                </>
              )}
            </h2>
          )}

          {phase === 'loading' && (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
            </div>
          )}

          {phase === 'error' && (
            <p className="mt-4 text-sm text-gray-500 text-center">
              Pending uploads could not be loaded.
            </p>
          )}

          {hasPending && (
            <>
              <h2 className="mt-8 text-lg font-semibold text-gray-900 pr-8">
                You have {pendingUploadCountLabel(pending.length)} pending uploads waiting for your review.
              </h2>
              {pending.length > VISIBLE_PENDING_UPLOADS && (
                <p className="text-sm text-gray-500 mt-1">Showing the 3 most recent...</p>
              )}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {visiblePending.map((item) => (
                  <IngestionActivityCard
                    key={item.id}
                    processId={item.id}
                    fileName={item.fileName}
                    status={item.status}
                    initiatedAt={item.initiatedAt}
                  />
                ))}
              </div>
              <PendingUploadsLink />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
